import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Table,
  Pagination,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import {
  FaFileExcel,
  FaSearch,
  FaEraser,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';
import { createDate, salvarEmExcel } from '~/Utils/Helpers';
import * as constants from '~/constant';

const ITEMS_PER_PAGE = 50;

const ORDEM_OPCOES = [
  { value: 1, label: 'Família' },
  { value: 2, label: 'Imóvel' },
  { value: 3, label: 'Cliente' },
  { value: 4, label: 'Data' },
  { value: 5, label: 'GE' },
];

const primeiroDiaMesAtual = () => {
  const now = new Date();
  return `01/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
};

const hoje = () => {
  const now = new Date();
  return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
};

const formatarData = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
};

const FILTROS_INICIAIS = {
  dataInicial: primeiroDiaMesAtual(),
  dataFinal: hoje(),
  criterioPorBoleto: true,
  incluirNaoVendedores: false,
  listarFones: false,
  incluirPreVendas: false,
  apenasComEmail: false,
  apenasMultiplosLotes: false,
  excluirTransferencias: true,
  apenasUltimasTransferencias: false,
  ordemResultado: 1,
  pagina: 1,
  porPagina: ITEMS_PER_PAGE,
};

export default function ImoveisVendidos() {
  // GE multi-select
  const [gesOptions, setGesOptions] = useState([]);
  const [gesSelecionados, setGesSelecionados] = useState([]);

  // Lookups
  const [opcoesGrupoGE, setOpcoesGrupoGE] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [ramos, setRamos] = useState([]);
  const [familiaSelected, setFamiliaSelected] = useState(null);
  const [vendedorSelected, setVendedorSelected] = useState(null);
  const [ramoSelected, setRamoSelected] = useState(null);

  // Filtros e resultado
  const [filtros, setFiltros] = useState({ ...FILTROS_INICIAIS });
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [ordenacao, setOrdenacao] = useState({ campo: '', direcao: 'asc' });

  // Carregar dados no mount
  useEffect(() => {
    api.listarGruposEmpresaAdmin({ status: 'A' })
      .then((resp) => {
        const lista = Array.isArray(resp) ? resp : [];
        setGesOptions(lista.map(ge => ({ value: ge.codigo, label: ge.apelido })));
      })
      .catch(() => toast.error('Erro ao carregar GEs.'));

    api.listarGruposGELookup()
      .then((resp) => {
        const lista = Array.isArray(resp) ? resp : [];
        setOpcoesGrupoGE(lista.map(g => ({ value: g.codigo, label: g.descricao })));
      })
      .catch(() => {});

    api.listarRamos()
      .then((resp) => {
        const lista = Array.isArray(resp) ? resp : [];
        setRamos(lista.map(r => ({ value: r.codigo, label: r.descricao })));
      })
      .catch(() => {});
  }, []);

  // Cascade: GEs selecionados → família + vendedor
  useEffect(() => {
    let ativo = true;
    if (gesSelecionados.length === 0) {
      setFamilias([]);
      setVendedores([]);
      setFamiliaSelected(null);
      setVendedorSelected(null);
      return;
    }
    const gesStr = gesSelecionados.map(g => g.value).join(',');
    Promise.all([
      api.buscarFamiliasImoveisVendidos(gesStr),
      api.buscarVendedoresImoveisVendidos(gesStr),
    ])
      .then(([resFam, resVend]) => {
        if (!ativo) return;
        const listFam = Array.isArray(resFam) ? resFam : [];
        const listVend = Array.isArray(resVend) ? resVend : [];
        setFamilias(listFam.map(f => ({ value: f.codigo_familia, label: f.nome_da_familia })));
        setVendedores(listVend.map(v => ({ value: v.codigo_conta, label: v.nome_prestador })));
        setFamiliaSelected(null);
        setVendedorSelected(null);
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, [gesSelecionados]);

  const handleChangeGes = (selected) => {
    setGesSelecionados(selected || []);
    setResultado(null);
  };

  // --- Validação e consulta ---
  const toServerDate = useCallback((str) => {
    if (!str) return null;
    const d = createDate(str, constants.DATE_DEFAULT_FORMAT);
    return d.isValid() ? d.format(constants.DATE_FORMAT_SERVER) : null;
  }, []);

  const validar = useCallback(() => {
    if (gesSelecionados.length === 0) {
      toast.warning('Selecione ao menos um GE.');
      return false;
    }
    if (!filtros.dataInicial || !filtros.dataFinal) {
      toast.warning('Preencha as datas de início e fim.');
      return false;
    }
    const di = createDate(filtros.dataInicial, constants.DATE_DEFAULT_FORMAT);
    const df = createDate(filtros.dataFinal, constants.DATE_DEFAULT_FORMAT);
    if (!di.isValid() || !df.isValid()) {
      toast.warning('Datas inválidas.');
      return false;
    }
    if (df.isBefore(di)) {
      toast.warning('Data final deve ser maior ou igual à data inicial.');
      return false;
    }
    return true;
  }, [gesSelecionados, filtros.dataInicial, filtros.dataFinal]);

  const montarPayload = useCallback((paginaOverride) => ({
    ges: gesSelecionados.map(g => g.value),
    data_inicial: toServerDate(filtros.dataInicial),
    data_final: toServerDate(filtros.dataFinal),
    codigo_familia: familiaSelected != null ? familiaSelected.value : null,
    codigo_prestador: vendedorSelected != null ? vendedorSelected.value : null,
    codigo_ramo: ramoSelected != null ? ramoSelected.value : null,
    criterio_por_boleto: filtros.criterioPorBoleto,
    incluir_nao_vendedores: filtros.incluirNaoVendedores,
    incluir_pre_vendas: filtros.incluirPreVendas,
    apenas_com_email: filtros.apenasComEmail,
    apenas_multiplos_lotes: filtros.apenasMultiplosLotes,
    excluir_transferencias: filtros.excluirTransferencias,
    apenas_ultimas_transferencias: filtros.apenasUltimasTransferencias,
    ordem_resultado: filtros.ordemResultado,
    pagina: paginaOverride != null ? paginaOverride : filtros.pagina,
    por_pagina: filtros.porPagina,
  }), [
    gesSelecionados, toServerDate, filtros,
    familiaSelected, vendedorSelected, ramoSelected,
  ]);

  const gerarRelatorio = async () => {
    if (!validar()) return;
    setLoading(true);
    setResultado(null);
    setFiltros(prev => ({ ...prev, pagina: 1 }));
    setOrdenacao({ campo: '', direcao: 'asc' });
    try {
      const resp = await api.consultarImoveisVendidos(montarPayload(1));
      if (!resp || resp.total === 0) {
        toast.info('Nenhum registro encontrado para os filtros selecionados.');
        setResultado(null);
      } else {
        if (resp.total_inconsistencias > 0) {
          toast.warning(
            `Atenção: ${resp.total_inconsistencias} imóvel(is) com status inconsistente detectado(s).`
          );
        }
        setResultado(resp);
      }
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao buscar dados.';
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };

  const mudarPagina = async (novaPagina) => {
    if (!validar()) return;
    setLoading(true);
    setFiltros(prev => ({ ...prev, pagina: novaPagina }));
    try {
      const resp = await api.consultarImoveisVendidos(montarPayload(novaPagina));
      setResultado(resp);
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao buscar dados.';
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    if (!resultado || resultado.total === 0) return;
    setExportando(true);
    try {
      const resp = await api.consultarImoveisVendidos({
        ...montarPayload(1),
        pagina: 1,
        por_pagina: 9999,
      });
      const items = (resp && resp.items) ? resp.items : [];
      if (items.length === 0) {
        toast.info('Nenhum registro para exportar.');
        return;
      }
      await salvarEmExcel(items, '/exportador/excel/relatorio-imoveis-vendidos');
    } catch (err) {
      const mensagem = err && err.message ? err.message : 'Erro ao exportar Excel.';
      toast.error(mensagem);
    } finally {
      setExportando(false);
    }
  };

  const limpar = () => {
    setGesSelecionados([]);
    setFamiliaSelected(null);
    setVendedorSelected(null);
    setRamoSelected(null);
    setFiltros({ ...FILTROS_INICIAIS });
    setResultado(null);
    setOrdenacao({ campo: '', direcao: 'asc' });
  };

  const handleOrdenacao = (campo) => {
    setOrdenacao(prev =>
      prev.campo === campo
        ? { campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' }
        : { campo, direcao: 'asc' }
    );
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const itensPaginados = useMemo(() => {
    if (!resultado || !resultado.items) return [];
    if (!ordenacao.campo) return resultado.items;
    return [...resultado.items].sort((a, b) => {
      const va = a[ordenacao.campo];
      const vb = b[ordenacao.campo];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return ordenacao.direcao === 'asc' ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      if (sa < sb) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (sa > sb) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });
  }, [resultado, ordenacao]);

  const totalPaginas = resultado ? Math.ceil(resultado.total / ITEMS_PER_PAGE) : 0;

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const pagAtual = filtros.pagina;
    const items = [];
    items.push(
      <Pagination.First key="first" onClick={() => mudarPagina(1)} disabled={pagAtual === 1 || loading} />,
      <Pagination.Prev key="prev" onClick={() => mudarPagina(pagAtual - 1)} disabled={pagAtual === 1 || loading} />,
    );
    const ini = Math.max(1, pagAtual - 2);
    const fim = Math.min(totalPaginas, pagAtual + 2);
    if (ini > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let i = ini; i <= fim; i++) {
      items.push(
        <Pagination.Item key={i} active={i === pagAtual} onClick={() => mudarPagina(i)}>
          {i}
        </Pagination.Item>,
      );
    }
    if (fim < totalPaginas) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" onClick={() => mudarPagina(pagAtual + 1)} disabled={pagAtual === totalPaginas || loading} />,
      <Pagination.Last key="last" onClick={() => mudarPagina(totalPaginas)} disabled={pagAtual === totalPaginas || loading} />,
    );
    return <Pagination size="sm" className="mb-0 flex-wrap">{items}</Pagination>;
  };

  const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
  const colunasFones = filtros.listarFones;

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Imóveis Vendidos" />

      <Card className="shadow-sm mb-3">
        <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff', fontWeight: 'bold' }}>
          Filtros
        </Card.Header>
        <Card.Body className="py-2">

          {/* GE / Grupo GE */}
          <Row className="mb-2">
            <Col md={8}>
              <Form.Label className="font-weight-bold mb-1">GE</Form.Label>
              <Select
                isMulti
                placeholder="Selecione um ou mais GEs..."
                options={gesOptions}
                value={gesSelecionados}
                onChange={handleChangeGes}
                styles={customStyles}
                components={{ DropdownIndicator }}
                isClearable
                closeMenuOnSelect={false}
                noOptionsMessage={() => 'Nenhum GE disponível'}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="font-weight-bold mb-1">Grupo GE</Form.Label>
              <Select
                placeholder="Grupo GE..."
                options={opcoesGrupoGE}
                styles={customStyles}
                components={{ DropdownIndicator }}
                isClearable
                noOptionsMessage={() => 'Nenhum grupo disponível'}
                onChange={() => {}}
              />
            </Col>
          </Row>

          {/* Família / Vendedor / Ramo */}
          <Row className="mb-2">
            <Col md={4}>
              <Form.Label className="font-weight-bold mb-1">Família</Form.Label>
              <Select
                placeholder="Todas as famílias"
                options={familias}
                value={familiaSelected}
                onChange={setFamiliaSelected}
                styles={customStyles}
                components={{ DropdownIndicator }}
                isClearable
                isDisabled={gesSelecionados.length === 0}
                noOptionsMessage={() => 'Selecione um GE primeiro'}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="font-weight-bold mb-1">Vendedor</Form.Label>
              <Select
                placeholder="Todos os vendedores"
                options={vendedores}
                value={vendedorSelected}
                onChange={setVendedorSelected}
                styles={customStyles}
                components={{ DropdownIndicator }}
                isClearable
                isDisabled={gesSelecionados.length === 0}
                noOptionsMessage={() => 'Selecione um GE primeiro'}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="font-weight-bold mb-1">Ramo de Atividade</Form.Label>
              <Select
                placeholder="Todos os ramos"
                options={ramos}
                value={ramoSelected}
                onChange={setRamoSelected}
                styles={customStyles}
                components={{ DropdownIndicator }}
                isClearable
                noOptionsMessage={() => 'Nenhum ramo disponível'}
              />
            </Col>
          </Row>

          {/* Período */}
          <Row className="mb-2">
            <Col md={3}>
              <Form.Label className="font-weight-bold mb-1">Período referente</Form.Label>
              <div>
                <Form.Check
                  inline type="radio" label="Data Boleto"
                  id="radio-boleto" name="criterioPeriodo"
                  checked={filtros.criterioPorBoleto}
                  onChange={() => setFiltros(prev => ({ ...prev, criterioPorBoleto: true }))}
                />
                <Form.Check
                  inline type="radio" label="Data Proposta"
                  id="radio-proposta" name="criterioPeriodo"
                  checked={!filtros.criterioPorBoleto}
                  onChange={() => setFiltros(prev => ({ ...prev, criterioPorBoleto: false }))}
                />
              </div>
            </Col>
            <Col md={3}>
              <Form.Label className="font-weight-bold mb-1">Data Inicial</Form.Label>
              <MaskedInput
                mask={constants.MASK_DATE}
                className="form-control"
                style={{ fontSize: '0.875rem', height: '31px', padding: '2px 8px' }}
                placeholder="dd/mm/aaaa"
                value={filtros.dataInicial}
                onChange={e => {
                  const val = e.target.value;
                  setFiltros(prev => ({ ...prev, dataInicial: val }));
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="font-weight-bold mb-1">Data Final</Form.Label>
              <MaskedInput
                mask={constants.MASK_DATE}
                className="form-control"
                style={{ fontSize: '0.875rem', height: '31px', padding: '2px 8px' }}
                placeholder="dd/mm/aaaa"
                value={filtros.dataFinal}
                onChange={e => {
                  const val = e.target.value;
                  setFiltros(prev => ({ ...prev, dataFinal: val }));
                }}
              />
            </Col>
          </Row>

          {/* Checkboxes linha 1 */}
          <Row className="mb-1">
            <Col md={4}>
              <Form.Check
                type="checkbox" id="chk-nao-vendedores"
                label="Incluir vendas de não-vendedores"
                checked={filtros.incluirNaoVendedores}
                onChange={e => {
                  const checked = e.target.checked;
                  setFiltros(prev => ({ ...prev, incluirNaoVendedores: checked }));
                }}
              />
            </Col>
            <Col md={4}>
              <Form.Check
                type="checkbox" id="chk-fones"
                label="Lista fones/email"
                checked={filtros.listarFones}
                onChange={e => {
                  const checked = e.target.checked;
                  setFiltros(prev => ({ ...prev, listarFones: checked }));
                }}
              />
            </Col>
            <Col md={4}>
              <Form.Check
                type="checkbox" id="chk-prevenda"
                label="Lista pré-vendas"
                checked={filtros.incluirPreVendas}
                onChange={e => {
                  const checked = e.target.checked;
                  setFiltros(prev => ({ ...prev, incluirPreVendas: checked }));
                }}
              />
            </Col>
          </Row>

          {/* Checkboxes linha 2 */}
          <Row className="mb-1">
            <Col md={4}>
              <Form.Check
                type="checkbox" id="chk-email"
                label="Somente clientes com email"
                checked={filtros.apenasComEmail}
                onChange={e => {
                  const checked = e.target.checked;
                  setFiltros(prev => ({ ...prev, apenasComEmail: checked }));
                }}
              />
            </Col>
            <Col md={4}>
              <Form.Check
                type="checkbox" id="chk-mais-lotes"
                label="Somente clientes com mais de 1 lote"
                checked={filtros.apenasMultiplosLotes}
                onChange={e => {
                  const checked = e.target.checked;
                  setFiltros(prev => ({ ...prev, apenasMultiplosLotes: checked }));
                }}
              />
            </Col>
            <Col md={4}>
              <Form.Check
                type="checkbox" id="chk-transf"
                label="Não listar transferências"
                checked={filtros.excluirTransferencias}
                onChange={e => {
                  const checked = e.target.checked;
                  setFiltros(prev => ({
                    ...prev,
                    excluirTransferencias: checked,
                    apenasUltimasTransferencias: checked
                      ? false
                      : prev.apenasUltimasTransferencias,
                  }));
                }}
              />
            </Col>
          </Row>

          {/* Checkbox condicional — visível somente quando excluirTransferencias está DESMARCADO */}
          {!filtros.excluirTransferencias && (
            <Row className="mb-1">
              <Col md={4} className="offset-md-8">
                <Form.Check
                  type="checkbox" id="chk-ultima-transf"
                  label="Somente últimas transferências"
                  checked={filtros.apenasUltimasTransferencias}
                  onChange={e => {
                    const checked = e.target.checked;
                    setFiltros(prev => ({
                      ...prev,
                      apenasUltimasTransferencias: checked,
                    }));
                  }}
                />
              </Col>
            </Row>
          )}

          {/* Ordem */}
          <Row className="mb-2">
            <Col>
              <Form.Label className="font-weight-bold mb-1">Ordem</Form.Label>
              <div>
                {ORDEM_OPCOES.map(opt => (
                  <Form.Check
                    key={opt.value}
                    inline type="radio"
                    label={opt.label}
                    id={`radio-ordem-${opt.value}`}
                    name="ordemResultado"
                    checked={filtros.ordemResultado === opt.value}
                    onChange={() => setFiltros(prev => ({ ...prev, ordemResultado: opt.value }))}
                  />
                ))}
              </div>
            </Col>
          </Row>

          {/* Botões */}
          <Row>
            <Col className="text-right">
              <Button
                variant="primary"
                className="mr-2"
                onClick={gerarRelatorio}
                disabled={loading || exportando}
              >
                {loading
                  ? <Spinner animation="border" size="sm" className="mr-1" />
                  : <FaSearch className="mr-1" />}
                Gerar Relatório
              </Button>
              <Button
                variant="success"
                className="mr-2"
                onClick={exportarExcel}
                disabled={loading || exportando || !resultado || resultado.total === 0}
              >
                {exportando
                  ? <Spinner animation="border" size="sm" className="mr-1" />
                  : <FaFileExcel className="mr-1" />}
                Exportar Excel
              </Button>
              <Button
                variant="secondary"
                onClick={limpar}
                disabled={loading || exportando}
              >
                <FaEraser className="mr-1" />
                Limpar
              </Button>
            </Col>
          </Row>

        </Card.Body>
      </Card>

      {/* Resultado */}
      {resultado && resultado.total > 0 && (
        <Card className="shadow-sm imoveis-vend-grid-card">
          <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff', fontWeight: 'bold' }}>
            Resultado | {resultado.total} registro(s)
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 mb-0">Carregando...</p>
              </div>
            ) : (
              <>
                <div className="imoveis-vend-desktop">
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th style={thStyle} onClick={() => handleOrdenacao('nome_familia')}>
                          Família {iconeOrdenacao('nome_familia')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('nome_ativo')}>
                          Imóvel {iconeOrdenacao('nome_ativo')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('apelido_conta')}>
                          Cliente {iconeOrdenacao('apelido_conta')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('endereco')}>
                          Endereço {iconeOrdenacao('endereco')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('bairro')}>
                          Bairro {iconeOrdenacao('bairro')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('cep')}>
                          CEP {iconeOrdenacao('cep')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('mapa1')}>
                          Mapa {iconeOrdenacao('mapa1')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('area_total')}>
                          Área {iconeOrdenacao('area_total')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('cpf')}>
                          CPF {iconeOrdenacao('cpf')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('rg')}>
                          RG {iconeOrdenacao('rg')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('cad_prefeitura')}>
                          Cad. Pref. {iconeOrdenacao('cad_prefeitura')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('data')}>
                          Data {iconeOrdenacao('data')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('qtipo')}>
                          Tipo {iconeOrdenacao('qtipo')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('status_imovel')}>
                          Status {iconeOrdenacao('status_imovel')}
                        </th>
                        <th style={thStyle} onClick={() => handleOrdenacao('ge_apelido')}>
                          GE {iconeOrdenacao('ge_apelido')}
                        </th>
                        {colunasFones && (
                          <th style={thStyle} onClick={() => handleOrdenacao('fone_1')}>
                            Fone 1 {iconeOrdenacao('fone_1')}
                          </th>
                        )}
                        {colunasFones && (
                          <th style={thStyle} onClick={() => handleOrdenacao('fone_2')}>
                            Fone 2 {iconeOrdenacao('fone_2')}
                          </th>
                        )}
                        {colunasFones && (
                          <th style={thStyle} onClick={() => handleOrdenacao('fax')}>
                            Fax {iconeOrdenacao('fax')}
                          </th>
                        )}
                        {colunasFones && (
                          <th style={thStyle} onClick={() => handleOrdenacao('celular_contato')}>
                            Celular {iconeOrdenacao('celular_contato')}
                          </th>
                        )}
                        {colunasFones && (
                          <th style={thStyle} onClick={() => handleOrdenacao('email')}>
                            Email {iconeOrdenacao('email')}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {itensPaginados.map(item => (
                        <tr
                          key={item.codigo_negocio}
                          style={item.inconsistencia_status
                            ? { backgroundColor: '#fff3cd' }
                            : undefined}
                        >
                          <td style={{ whiteSpace: 'nowrap' }}>{item.nome_familia}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{item.nome_ativo}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {item.codigo_conta}-{item.apelido_conta}
                          </td>
                          <td>{item.endereco}</td>
                          <td>{item.bairro}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{item.cep}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {item.mapa1 != null ? item.mapa1 : ''}/{item.mapa2 != null ? item.mapa2 : ''}
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {item.area_total}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{item.cpf}</td>
                          <td>{item.rg}</td>
                          <td>{item.cad_prefeitura}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatarData(item.data)}</td>
                          <td>{item.qtipo}</td>
                          <td>{item.status_imovel}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{item.ge_apelido}</td>
                          {colunasFones && <td>{item.fone_1}</td>}
                          {colunasFones && <td>{item.fone_2}</td>}
                          {colunasFones && <td>{item.fax}</td>}
                          {colunasFones && <td>{item.celular_contato}</td>}
                          {colunasFones && <td>{item.email}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {totalPaginas > 1 && (
                  <div className="d-flex justify-content-between align-items-center px-3 py-2">
                    <small className="text-muted">
                      Mostrando {((filtros.pagina - 1) * ITEMS_PER_PAGE) + 1} até{' '}
                      {Math.min(filtros.pagina * ITEMS_PER_PAGE, resultado.total)} de{' '}
                      {resultado.total} registros
                    </small>
                    {renderPaginacao()}
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      )}

      <style>{`
        .imoveis-vend-desktop   { display: block; overflow-x: auto; }
        .imoveis-vend-grid-card { max-width: calc(100vw - 50px); }
        @media (max-width: 768px) {
          .imoveis-vendidos-table { font-size: 0.75rem; }
        }
      `}</style>
    </Container>
  );
}
